import React, { FunctionComponent, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { CALENDAR_EVENT_VIDEO_PLATFORM_DISPLAY_NAME } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/appointmentModalityConstants'
import { CheckboxButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { DEFAULT_VIDEO_PLATFORM, VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { Checkbox } from '../../atoms/formElements/Checkbox'
import { SelectField } from '../../atoms/formElements/SelectField'
import { tID } from '../../utils/utils'

export type ProviderCalendarModalitySelectorProps = {
  addresses?: Address[] | null
  inPersonModality: boolean
  inPersonPreference?: boolean
  splitVideoOptions?: boolean
  selectedLocation?: Address
  selectedVideoPlatform?: VIDEO_PLATFORM | null
  setInPersonModality: (inPerson: boolean) => void
  setSelectedLocation: (address: Address | undefined) => void
  setSelectedVideoPlatform?: (platform: VIDEO_PLATFORM) => void
  setVideoModality: (virtual: boolean) => void
  videoModality: boolean
  videoPreference?: boolean
}

const RowContainer = styled.View(({ theme }) => ({
  height: theme.spacing['32px'],
  paddingTop: theme.spacing['8px'],
  display: 'flex',
  flexDirection: 'row',
  backgroundColor: theme.colors.backgroundPrimary,
}))

const SelectFieldContainer = styled.View(({ theme }) => ({
  paddingLeft: '38px',
  paddingTop: theme.spacing['4px'],
}))

const VIDEO_PLATFORM_OPTIONS: { label: string; value: VIDEO_PLATFORM }[] = [
  { label: CALENDAR_EVENT_VIDEO_PLATFORM_DISPLAY_NAME[VIDEO_PLATFORM.LYRA], value: VIDEO_PLATFORM.LYRA },
  { label: CALENDAR_EVENT_VIDEO_PLATFORM_DISPLAY_NAME[VIDEO_PLATFORM.EXTERNAL], value: VIDEO_PLATFORM.EXTERNAL },
]

export const ProviderCalendarModalitySelector: FunctionComponent<ProviderCalendarModalitySelectorProps> = ({
  addresses,
  inPersonModality,
  inPersonPreference,
  splitVideoOptions,
  selectedLocation,
  selectedVideoPlatform,
  setInPersonModality,
  setSelectedLocation,
  setSelectedVideoPlatform,
  setVideoModality,
  videoModality,
  videoPreference,
}) => {
  const { spacing } = useTheme()

  const addressOptions = useMemo(() => {
    let addressIdPlaceholder = 0
    return (
      addresses?.map((address) => ({ label: address.street1 ?? '', value: address.id ?? addressIdPlaceholder++ })) ?? []
    )
  }, [addresses])

  const addressMap = useMemo(() => {
    const addressMap = new Map<number, Address>()
    let addressIdPlaceholder = 0
    addresses?.forEach((address) => {
      addressMap.set(address.id ?? addressIdPlaceholder++, address)
    })
    return addressMap
  }, [addresses])

  return (
    <View testID={tID('ProviderCalendarModalitySelector-Container')}>
      <BodyText
        text={
          <FormattedMessage
            defaultMessage='Modality'
            description='Title for selectors where providers select the modality to offer for an available time slot'
          />
        }
      />
      {videoPreference && (
        <>
          <RowContainer testID={tID('ProviderCalendarModalitySelector-VideoRow')}>
            <Checkbox
              buttonType={CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE}
              title={<FormattedMessage defaultMessage='Video' description='Label for selecting video modality' />}
              checked={videoModality}
              onPress={() => {
                setVideoModality(!videoModality)
              }}
              name='Video'
            />
          </RowContainer>
          {splitVideoOptions && videoModality && (
            <SelectFieldContainer testID={tID('ProviderCalendarModalitySelector-VideoPlatformSelectField')}>
              <SelectField
                disablePlaceholder={true}
                name='selectedVideoPlatform'
                accessibilityLabel='Video platform'
                onChange={(value: VIDEO_PLATFORM) => setSelectedVideoPlatform?.(value)}
                options={VIDEO_PLATFORM_OPTIONS}
                value={selectedVideoPlatform ?? DEFAULT_VIDEO_PLATFORM}
                baseInputStyle={{ marginBottom: spacing['8px'] }}
              />
            </SelectFieldContainer>
          )}
        </>
      )}
      {inPersonPreference && addresses && (
        <>
          <RowContainer testID={tID('ProviderCalendarModalitySelector-InPersonRow')}>
            <Checkbox
              buttonType={CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE}
              title={
                <FormattedMessage defaultMessage='In-person' description='Label for selecting in-person modality' />
              }
              checked={inPersonModality}
              onPress={() => {
                setInPersonModality(!inPersonModality)
              }}
              name='In-person'
            />
          </RowContainer>
          {inPersonModality && (
            <SelectFieldContainer testID={tID('ProviderCalendarModalitySelector-LocationSelectField')}>
              {addressOptions.length > 1 ? (
                <SelectField
                  disablePlaceholder={true}
                  name={`selectedLocation-${selectedLocation?.id ?? 0}`}
                  accessibilityLabel='Location'
                  onChange={(value: number) => setSelectedLocation(addressMap.get(+value))}
                  options={addressOptions}
                  value={selectedLocation?.id ?? 0}
                  baseInputStyle={{ marginBottom: spacing['8px'] }}
                />
              ) : (
                <BodyText
                  text={selectedLocation?.street1 ?? ''}
                  testID={tID('ProviderCalendarModalitySelector-LocationText')}
                />
              )}
            </SelectFieldContainer>
          )}
        </>
      )}
    </View>
  )
}
